var CoverVariables = require("app/theme/styles/covers/CoverVariables"),
    Layout         = require("enyo-ui/Layout"),
    SlideVariables = require("app/theme/styles/slides/SlideVariables"),
    dom            = require("enyo/dom"),
    kind           = require("enyo/kind"),
    themeVars      = require("app/theme/variables.js"),
    utils          = require("enyo/utils");

module.exports = kind(/** @lends  app.ui.header.MediaHeaderLayout.prototype */ {

    /**
     * @private
     */
    name: "app.ui.header.MediaHeaderLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * Default options for the layout.
     *
     * @type {Object}
     * @protected
     */
    options: /** @lends  app.ui.header.MediaHeaderLayout.prototype */ {

        /**
         * The size of the slider.
         *
         * Current options include:
         * - `default`  Default slides, used for media types which display a
         *              header above a dashboard or a list of serial content.
         * - `tall`     Tall slides, used for media types which display a
         *              viewport-filling headers for non-serial content.
         *
         * @type     {string|function}
         * @default  'default'
         * @public
         */
        sliderSize: "default",

        /**
         * The formFactor of the cover.
         *
         * Available options:
         *  - `landscape`
         *  - `square`
         *  - `portrait`
         *  - `null` (no cover)
         *
         * @return  {?string}  The formFactor of the cover
         * @default  `null`
         * @public
         */
        coverFormFactor: function() {
            return this.container.get("coverFormFactor") || "portrait";
        },

        /**
         * Some media types show a tall header. If those media items don't have
         * a dashboard, the layout will try to fill the screen. If they do have
         * a dashboard, we want to try and give the user has a peak at the
         * dashboard above the fold.
         *
         * @type     {boolean}
         * @default  'false'
         * @public
         */
        peekAtDashboard: false,

        /**
         * The minimum height of the peak we want to show at the dashboard.
         *
         * @type    {number|function}
         * @return  {number}  The height of the peak
         * @public
         */
        minDashboardPeek: function() {
            // The height of the title bar of a carouselRow or gridRow plus some
            // padding above and some cover content below.
            return themeVars.remToPx
                + themeVars.getContentPaddingVerticalPx()
                + themeVars.getHeaderLineHeightPx("h2")
                + themeVars.getContentPaddingVerticalPx()
                + themeVars.remToPx * 3;
        }
    },

    /**
     * @public
     */
    reflow: function() {
        var $container             = this.container,
            containerNode          = $container.hasNode(),
            $slider                = $container.$.slider,
            $actionButtons         = $container.$.actionButtons,
            $horizontalDescription = $container.$.horizontalDescription,
            $previousSlideButton   = $container.$.previousSlideButton,
            $nextSlideButton       = $container.$.nextSlideButton,
            $cover                 = $container.$.cover,
            containerFormFactor,
            showSlider,
            sliderFormFactor,
            sliderSize,
            sliderDimensions,
            showDescription,
            horizontalDescriptionWidth,
            showSliderButtons,
            previousSlideButtonBounds,
            nextSlideButtonBounds,
            showCover,
            coverArea              = {},
            coverFormFactor        = this.getOption("coverFormFactor"),
            coverBounds            = {};

        if (!containerNode) {
            return;
        }

        // Do we need to switch form-factor?
        containerFormFactor = themeVars.formFactor === "large" ? "horizontal" : "vertical";

        if (containerFormFactor !== $container.get("formFactor")) {
            $container.set("formFactor", containerFormFactor);

            // The Header should update it's state in response to the new
            // formFactor. As a result the UI might need to reflow differently.
            // At the end of the update cycle a new reflow will be triggered, so
            // we can afford to exit early over here.
            return;
        }

        // Compact header doesn't need further layout
        if ($container.get("showCompactHeader")) {
            return;
        }

        // Get toggles
        showSlider        = $container.get("showSlider");
        showDescription   = $container.get("showDescription");
        showSliderButtons = $container.get("showSliderButtons");
        showCover         = $container.get("showCover");

        // Reflow slider
        if (showSlider && $slider) {
            sliderFormFactor = this.calcSliderFormFactor();
            sliderSize       = this.getOption("sliderSize");
            sliderDimensions = sliderSize === "tall"
                ? this.calcTallSliderDimensions(sliderFormFactor)
                : this.calcDefaultSliderDimensions(sliderFormFactor);
            $slider.addStyles({
                width:  sliderDimensions.width + "px",
                height: sliderDimensions.height + "px"
            });
            $slider.set("aspectRatio", sliderDimensions.width / sliderDimensions.height);

            if (containerFormFactor === "horizontal") {
                $actionButtons.addStyles({
                    top:    null,
                    bottom: "0px"
                });
                dom.transformValue($actionButtons, "translateY", "0%");
            }
            else {
                $actionButtons.addStyles({
                    top:    sliderDimensions.height + "px",
                    bottom: null
                });
                dom.transformValue($actionButtons, "translateY", "-100%");
            }
        }


        // Calculate the width of the horizontal description
        if (showDescription
            && containerFormFactor === "horizontal"
            && $horizontalDescription
        ) {
            horizontalDescriptionWidth = $horizontalDescription.getBounds().width;
        }
        else {
            horizontalDescriptionWidth = 0;
        }

        // Reflow slider buttons
        if (showSliderButtons && $previousSlideButton) {
            previousSlideButtonBounds      = $previousSlideButton.getBounds();
            previousSlideButtonBounds.top  = (
                sliderDimensions.height - previousSlideButtonBounds.height
            ) / 2;
            previousSlideButtonBounds.left = horizontalDescriptionWidth > 0
                ? horizontalDescriptionWidth
                        + themeVars.getContentPaddingHorizontalPx()
                : themeVars.safeAreaInsetLeft
                        + themeVars.getPageMarginPx();
            $previousSlideButton.addStyles({
                top:  previousSlideButtonBounds.top + "px",
                left: previousSlideButtonBounds.left + "px"
            });
        }
        else {
            previousSlideButtonBounds = {
                width:  0,
                height: 0
            };
        }

        if (showSliderButtons && $nextSlideButton) {
            nextSlideButtonBounds       = $nextSlideButton.getBounds();
            nextSlideButtonBounds.top   = (
                sliderDimensions.height - nextSlideButtonBounds.height
            ) / 2;
            nextSlideButtonBounds.right = themeVars.safeAreaInsetRight
                    + themeVars.getPageMarginPx();
            $nextSlideButton.addStyles({
                top:   nextSlideButtonBounds.top + "px",
                right: nextSlideButtonBounds.right + "px"
            });
        }
        else {
            nextSlideButtonBounds = {
                width:  0,
                height: 0
            };
        }

        // Reflow cover
        if (showCover && $cover) {

            // Calculate the area available for the cover in horizontal header
            // formFactor
            if (containerFormFactor === "horizontal") {
                if (themeVars.appHeaderFloat === true) {
                    coverArea.top = this.getHeaderRegionHeight() + themeVars.getContentPaddingVerticalPx();
                }
                else {
                    coverArea.top = sliderDimensions.height * 0.125;
                }

                coverArea.right = themeVars.safeAreaInsetRight
                    + themeVars.getPageMarginPx();

                if (nextSlideButtonBounds.width > 0) {
                    coverArea.right += nextSlideButtonBounds.width
                        + themeVars.getContentPaddingHorizontalPx();
                }

                if (themeVars.appHeaderFloat === true) {
                    coverArea.bottom = themeVars.getContentPaddingVerticalPx() + themeVars.getButtonPaddingVerticalPx();
                }
                else {
                    coverArea.bottom = sliderDimensions.height * 0.125;
                }

                coverArea.left   = 0;

                if (horizontalDescriptionWidth > 0) {
                    coverArea.left += horizontalDescriptionWidth
                        + themeVars.getContentPaddingHorizontalPx();
                }
                else {
                    coverArea.left += themeVars.safeAreaInsetLeft
                        + themeVars.getPageMarginPx();
                }

                if (previousSlideButtonBounds.width > 0) {
                    coverArea.left += previousSlideButtonBounds.width
                        + themeVars.getContentPaddingHorizontalPx();
                }
            }

            // Calculate the area available for the cover in tall header
            // formFactor
            else {
                coverArea.top = themeVars.getContentPaddingVerticalPx();

                if (themeVars.appHeaderFloat === true) {
                    coverArea.top += this.getHeaderRegionHeight();
                }

                coverArea.right = themeVars.safeAreaInsetRight
                    + themeVars.getPageMarginPx();

                if (nextSlideButtonBounds.width > 0) {
                    coverArea.right += nextSlideButtonBounds.width
                        + themeVars.getContentPaddingHorizontalPx();
                }

                coverArea.bottom = themeVars.getContentPaddingVerticalPx();

                if (themeVars.appHeaderFloat === true) {
                    coverArea.bottom += themeVars.getContentPaddingVerticalPx();
                }

                coverArea.left   = themeVars.safeAreaInsetLeft
                    + themeVars.getPageMarginPx();

                if (previousSlideButtonBounds.width > 0) {
                    coverArea.left += previousSlideButtonBounds.width
                        + themeVars.getContentPaddingHorizontalPx();
                }
            }

            // Cover measurements
            coverArea.width         = sliderDimensions.width - coverArea.left - coverArea.right;
            coverArea.height        = sliderDimensions.height - coverArea.top - coverArea.bottom;
            coverArea.aspectRatio   = coverArea.width / coverArea.height;
            coverBounds.aspectRatio = coverFormFactor ? CoverVariables.getAspectRatio(coverFormFactor) : null;

            if (coverArea.aspectRatio >= coverBounds.aspectRatio) {
                coverBounds.height = coverArea.height;
                coverBounds.width  = coverBounds.height * coverBounds.aspectRatio;
            }
            else {
                coverBounds.width  = coverArea.width;
                coverBounds.height = coverBounds.width / coverBounds.aspectRatio;
            }

            // Place cover
            $cover.setBounds({
                top:    Math.round(coverArea.top + (coverArea.height - coverBounds.height) / 2) + "px",
                left:   Math.round(coverArea.left + (coverArea.width - coverBounds.width) / 2) + "px",
                width:  Math.round(coverBounds.width) + "px",
                height: Math.round(coverBounds.height) + "px"
            });
        }
    },

    /**
     * Get the height of the headerRegion.
     *
     * @return {number} The height
     */
    getHeaderRegionHeight: function() {
        var appView            = this.container.app.view,
            headerRegionBounds = appView.get("headerRegionBounds") || {};

        return headerRegionBounds.height || 0;
    },

    /**
     * @returns  {string}  The form factor
     * @private
     */
    calcSliderFormFactor: function() {
        var $container          = this.container,
            contentRegionBounds = $container.app.view.get("contentRegionBounds"),
            contentRegionHeight = contentRegionBounds.height,
            contentRegionWidth  = contentRegionBounds.width,
            contentRegionRatio  = contentRegionHeight / contentRegionWidth,
            sliderSize          = this.getOption("sliderSize"),
            formFactors         = SlideVariables.formFactors[sliderSize] || {},
            i,
            newFormFactor,
            sliderWidth,
            sliderHeight,
            sliderRatio,
            bestFormFactor,
            rejects             = [];

        // Sort from by width descending
        formFactors.sort(function(a, b) {
            return a.width > b.width ? -1 : 1;
        });

        // Find the lowest resolution formFactor that fits the following
        // requirements:
        //  - it fills at least 75% of the height of the contentRegion
        //  - it fills at least 100% of the width of the contentRegion
        for (i = 0; i < formFactors.length; i++) {
            newFormFactor = utils.clone(formFactors[i]);
            sliderWidth   = newFormFactor.width;
            sliderHeight  = newFormFactor.height;
            sliderRatio   = sliderHeight / sliderWidth;

            // Form-factor fits requirements
            if (sliderRatio < contentRegionRatio * 0.75 && sliderWidth >= contentRegionWidth) {
                bestFormFactor = newFormFactor;
            }
            else {
                rejects.push(newFormFactor);
            }
        }

        // No formFactors of sufficient resolution were found. Let's pick the
        // widest one we have instead. If the maximum width is shared among
        // multiple formFactors, pick the one that matches the desired aspect
        // ratio the the closest.
        if (!bestFormFactor) {
            // Sort by aspect ratio diff, descending
            rejects.sort(function(a, b) {
                return a.delta < b.delta ? -1 : 1;
            });

            for (i = 0; i < rejects.length; i++) {
                newFormFactor = rejects[i];

                // Calculate the resolution deficit
                newFormFactor.deficit = contentRegionWidth - newFormFactor.width;

                // First formFactor we encountered
                if (!bestFormFactor) {
                    bestFormFactor = newFormFactor;
                }

                // Better resolution.
                else if (newFormFactor.deficit < bestFormFactor.deficit) {
                    bestFormFactor = newFormFactor;
                }
            }
        }

        return bestFormFactor;
    },

    /**
     * @param    {Object}  sliderFormFactor  The form factor
     * @returns  {Object}  The slider dimensions
     * @private
     */
    calcDefaultSliderDimensions: function(sliderFormFactor) {
        var $container          = this.container,
            contentRegionBounds = $container.app.view.get("contentRegionBounds"),
            sliderWidth,
            sliderHeight;

        sliderWidth  = contentRegionBounds.width;
        sliderHeight = Math.round(sliderWidth / sliderFormFactor.aspectRatio);

        return {
            width:  sliderWidth,
            height: sliderHeight
        };
    },

    /**
     * @param    {Object}  sliderFormFactor The form factor
     * @returns  {Object}  The slider dimensions
     * @private
     */
    calcTallSliderDimensions: function(sliderFormFactor) {
        var $container          = this.container,
            contentRegionBounds = $container.app.view.get("contentRegionBounds"),
            peekAtDashboard     = this.getOption("peekAtDashboard"),
            sliderAspectRatio,
            sliderWidth,
            sliderHeight,
            idealSliderHeight;

        // Slider dimensions based on unaltered formFactor.
        sliderAspectRatio = sliderFormFactor.aspectRatio;
        sliderWidth       = contentRegionBounds.width;
        sliderHeight      = Math.round(sliderWidth / sliderAspectRatio);

        // Slider could potentially scale to fill the contentRegion.
        idealSliderHeight = contentRegionBounds.height;

        // Leave some room to peak at the dashboards as needed
        if (peekAtDashboard) {
            idealSliderHeight -= Math.max(
                contentRegionBounds.height * 0.1,
                this.getOption("minDashboardPeek")
            );
        }
        idealSliderHeight = Math.round(idealSliderHeight);

        // Slider isn't tall enough, maybe scale it up a little
        if (sliderHeight < idealSliderHeight) {

            // Calculate new sliderHeight
            sliderHeight = Math.min(

                // Use the ideal height if possible
                idealSliderHeight,

                // But don't go taller than square
                sliderWidth,

                // And don't cut off more than 10% on either side of the slider
                Math.round(sliderWidth / 0.8 / sliderAspectRatio)
            );
        }

        // Slider is too tall, maybe scale it down a little.
        if (sliderHeight > idealSliderHeight) {

            // Calculate new sliderHeight
            sliderHeight = Math.max(

                // Use the ideal height if possible
                idealSliderHeight,

                // But don't go less tall than 80% of native height
                Math.round(sliderHeight * 0.8),

                // And don't shrink further than 40% of sliderWidth
                Math.round(sliderWidth / 2.5)
            );
        }

        return {
            width:  sliderWidth,
            height: sliderHeight
        };
    }

});
